// ENG-2277 (HTML Forms): "Edit Form & Print" for a surface whose picked row is a Digital Form. A surface
// mounts it only then. The merge-tag data behind it (the patient, the signature images) is loaded by
// mounting the hook, so a dialog that only ever prints Anvil forms, or runs with the flag off, never
// asks for any of it.
import { forwardRef, useImperativeHandle } from "react";

import EditIcon from "@mui/icons-material/Edit";
import LoadingButton, { type LoadingButtonProps } from "@mui/lab/LoadingButton";

import { useSurfaceDigitalForm } from "./use-surface-digital-form.hook";

type SurfaceDigitalForm = ReturnType<typeof useSurfaceDigitalForm>;

/** What the surface keeps a ref to, so its form can open the Digital Form on Enter as well as on click. */
export type SurfaceDigitalFormHandle = Pick<SurfaceDigitalForm, "open">;

type SurfaceDigitalFormButtonProps = Omit<
  Parameters<typeof useSurfaceDigitalForm>[0],
  "enabled"
> &
  // A surface either submits its own form with the button (`type` and `form`) or handles the click.
  Pick<
    LoadingButtonProps,
    "disabled" | "size" | "sx" | "type" | "form" | "onClick"
  >;

const SurfaceDigitalFormButton = forwardRef<
  SurfaceDigitalFormHandle,
  SurfaceDigitalFormButtonProps
>(function SurfaceDigitalFormButton(
  { disabled, size, sx, type, form: formId, onClick, ...form },
  ref
) {
  const { isReady, isOpening, open } = useSurfaceDigitalForm({
    enabled: true,
    ...form
  });
  useImperativeHandle(ref, () => ({ open }), [open]);

  return (
    <LoadingButton
      variant="contained"
      size={size}
      disabled={disabled}
      loading={!isReady || isOpening}
      startIcon={<EditIcon />}
      type={type}
      form={formId}
      onClick={onClick}
      sx={sx}
    >
      Edit Form&nbsp;&amp;&nbsp;Print
    </LoadingButton>
  );
});

export default SurfaceDigitalFormButton;
